import { useEffect, useState } from "react";
import { Plus, Search, Phone, Mail, Calendar, Receipt } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import { toast } from "sonner";

const empty = {
  name: "", phone: "", email: "", address: "",
  device_purchased: "", subscription_plan: "",
  subscription_start: "", subscription_expiry: "",
  server_used: "", notes: "",
};

function fmtDate(s) {
  if (!s) return "—";
  try { return new Date(s).toLocaleDateString(); } catch { return s; }
}

export default function Customers() {
  const [customers, setCustomers] = useState([]);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editingId, setEditingId] = useState(null);
  const [detail, setDetail] = useState(null);

  const load = async (search = "") => {
    const { data } = await api.get("/customers", { params: search ? { q: search } : {} });
    setCustomers(data);
  };

  useEffect(() => { load(); }, []);

  const onSearch = (e) => {
    const v = e.target.value;
    setQ(v);
    load(v);
  };

  const openCreate = () => { setForm(empty); setEditingId(null); setOpen(true); };
  const openEdit = (c) => {
    setForm({
      ...empty, ...c,
      subscription_start: c.subscription_start ? c.subscription_start.slice(0, 10) : "",
      subscription_expiry: c.subscription_expiry ? c.subscription_expiry.slice(0, 10) : "",
    });
    setEditingId(c.id);
    setOpen(true);
  };

  const onSave = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form };
      if (!payload.email) delete payload.email;
      if (editingId) {
        await api.put(`/customers/${editingId}`, payload);
        toast.success("Customer updated");
      } else {
        await api.post("/customers", payload);
        toast.success("Customer added");
      }
      setOpen(false);
      load(q);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const openDetail = async (id) => {
    const { data } = await api.get(`/customers/${id}`);
    setDetail(data);
  };

  return (
    <div className="space-y-6" data-testid="customers-page">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <div>
          <h1 className="font-heading text-3xl font-semibold text-navy">Customers</h1>
          <p className="text-sm text-muted-foreground">Manage subscribers and purchase history.</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input data-testid="customer-search-input" placeholder="Search name or phone..."
                   value={q} onChange={onSearch} className="pl-9 w-72" />
          </div>
          <Button data-testid="add-customer-btn" onClick={openCreate} className="bg-navy hover:bg-navy-hover text-white">
            <Plus className="w-4 h-4 mr-1" /> Add Customer
          </Button>
        </div>
      </div>

      <Card className="p-0 overflow-hidden section-card">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-muted/60 text-muted-foreground text-xs uppercase tracking-wider">
              <tr>
                <th className="text-left px-4 py-3">Name</th>
                <th className="text-left px-4 py-3">Contact</th>
                <th className="text-left px-4 py-3">Device</th>
                <th className="text-left px-4 py-3">Subscription</th>
                <th className="text-left px-4 py-3">Expiry</th>
                <th className="text-left px-4 py-3">Server</th>
                <th className="text-right px-4 py-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {customers.length === 0 ? (
                <tr><td colSpan={7} className="px-4 py-10 text-center text-muted-foreground">No customers yet. Add your first one.</td></tr>
              ) : customers.map((c) => {
                const expired = c.subscription_expiry && new Date(c.subscription_expiry) < new Date();
                return (
                  <tr key={c.id} className="border-t border-border/60 hover:bg-muted/40" data-testid={`customer-row-${c.id}`}>
                    <td className="px-4 py-3 font-medium text-navy">{c.name}</td>
                    <td className="px-4 py-3 text-muted-foreground">
                      <div className="flex items-center gap-1"><Phone className="w-3 h-3" />{c.phone}</div>
                      {c.email && <div className="flex items-center gap-1 text-xs"><Mail className="w-3 h-3" />{c.email}</div>}
                    </td>
                    <td className="px-4 py-3">{c.device_purchased || "—"}</td>
                    <td className="px-4 py-3">{c.subscription_plan || "—"}</td>
                    <td className="px-4 py-3">
                      {c.subscription_expiry ? (
                        <Badge className={expired ? "bg-destructive text-white" : "bg-emerald-100 text-emerald-700"}>
                          {fmtDate(c.subscription_expiry)}
                        </Badge>
                      ) : "—"}
                    </td>
                    <td className="px-4 py-3 text-xs text-muted-foreground">{c.server_used || "—"}</td>
                    <td className="px-4 py-3 text-right space-x-2">
                      <Button size="sm" variant="ghost" data-testid={`view-customer-${c.id}`} onClick={() => openDetail(c.id)}>
                        <Receipt className="w-3 h-3 mr-1" /> History
                      </Button>
                      <Button size="sm" variant="outline" data-testid={`edit-customer-${c.id}`} onClick={() => openEdit(c)}>
                        Edit
                      </Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Add/Edit Dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl" data-testid="customer-dialog">
          <DialogHeader>
            <DialogTitle>{editingId ? "Edit Customer" : "Add Customer"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={onSave} className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label>Full name *</Label>
              <Input required data-testid="cust-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label>Phone *</Label>
              <Input required data-testid="cust-phone-input" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label>Email</Label>
              <Input type="email" data-testid="cust-email-input" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label>Address</Label>
              <Input data-testid="cust-address-input" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label>Device purchased</Label>
              <Input data-testid="cust-device-input" value={form.device_purchased} onChange={(e) => setForm({ ...form, device_purchased: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label>Subscription plan</Label>
              <Input data-testid="cust-plan-input" value={form.subscription_plan} onChange={(e) => setForm({ ...form, subscription_plan: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label>Subscription start</Label>
              <Input type="date" data-testid="cust-start-input" value={form.subscription_start || ""} onChange={(e) => setForm({ ...form, subscription_start: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label>Subscription expiry</Label>
              <Input type="date" data-testid="cust-expiry-input" value={form.subscription_expiry || ""} onChange={(e) => setForm({ ...form, subscription_expiry: e.target.value })} />
            </div>
            <div className="space-y-1.5 md:col-span-2">
              <Label>Server used</Label>
              <Input data-testid="cust-server-input" value={form.server_used} onChange={(e) => setForm({ ...form, server_used: e.target.value })} placeholder="e.g. Server NA-East-3" />
            </div>
            <div className="space-y-1.5 md:col-span-2">
              <Label>Notes</Label>
              <Textarea data-testid="cust-notes-input" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={3} />
            </div>
            <DialogFooter className="md:col-span-2">
              <Button type="button" variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
              <Button type="submit" data-testid="save-customer-btn" className="bg-navy hover:bg-navy-hover text-white">
                {editingId ? "Save changes" : "Add customer"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Detail Dialog */}
      <Dialog open={!!detail} onOpenChange={() => setDetail(null)}>
        <DialogContent className="max-w-2xl" data-testid="customer-detail-dialog">
          <DialogHeader>
            <DialogTitle>{detail?.customer?.name}</DialogTitle>
          </DialogHeader>
          {detail && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div><span className="text-muted-foreground">Phone:</span> {detail.customer.phone}</div>
                <div><span className="text-muted-foreground">Email:</span> {detail.customer.email || "—"}</div>
                <div><span className="text-muted-foreground">Device:</span> {detail.customer.device_purchased || "—"}</div>
                <div><span className="text-muted-foreground">Plan:</span> {detail.customer.subscription_plan || "—"}</div>
                <div><span className="text-muted-foreground">Start:</span> {fmtDate(detail.customer.subscription_start)}</div>
                <div><span className="text-muted-foreground">Expiry:</span> {fmtDate(detail.customer.subscription_expiry)}</div>
                <div className="col-span-2"><span className="text-muted-foreground">Server:</span> {detail.customer.server_used || "—"}</div>
                {detail.customer.notes && <div className="col-span-2"><span className="text-muted-foreground">Notes:</span> {detail.customer.notes}</div>}
              </div>
              <div>
                <h4 className="font-heading text-sm font-semibold text-navy mb-2">Purchase History ({detail.sales.length})</h4>
                {detail.sales.length === 0 ? (
                  <div className="text-sm text-muted-foreground">No purchases yet.</div>
                ) : (
                  <div className="border border-border/60 rounded divide-y divide-border/60 max-h-64 overflow-auto">
                    {detail.sales.map((s) => (
                      <div key={s.id} className="px-3 py-2 text-sm flex justify-between">
                        <div>
                          <div>{new Date(s.created_at).toLocaleString()}</div>
                          <div className="text-xs text-muted-foreground">{s.items.map(i => `${i.product_name} x${i.quantity}`).join(", ")}</div>
                        </div>
                        <div className="font-semibold text-navy">${s.total.toFixed(2)}</div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
